import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const ACCENT = '#4d9fff';

const Container = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem 1.75rem 2rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 22% 18%, rgba(77, 159, 255, 0.18), transparent 48%),
      radial-gradient(circle at 78% 22%, rgba(77, 159, 255, 0.1), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 2rem 1.1rem 1.6rem;
    margin-bottom: 1.5rem;
    border-radius: 18px;
  }
`;

const Title = styled.h1`
  font-size: 2.6rem;
  color: rgb(var(--fg) / 0.96);
  margin: 0;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.03em;

  @media (max-width: 600px) {
    font-size: 1.85rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: rgb(var(--fg) / 0.72);
  margin: 14px auto 14px;
  line-height: 1.65;
  max-width: 720px;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const DatePublished = styled.p`
  font-size: 0.82rem;
  color: rgb(var(--gray) / 0.6);
  margin: 0 0 18px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  font-size: 0.78rem;
  padding: 6px 13px;
  border-radius: 999px;
  font-weight: 600;
  border: 1px solid rgba(77, 159, 255, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s, border-color 0.2s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.08);
    border-color: rgba(77, 159, 255, 0.55);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
  padding: 1.75rem 1.6rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 600px) {
    margin-bottom: 1.1rem;
    padding: 1.25rem 1.05rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  color: ${ACCENT};
  margin: 0 0 14px;
  border-bottom: 1px solid rgba(77, 159, 255, 0.25);
  padding-bottom: 10px;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.15rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin: 0 0 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  em {
    color: rgb(var(--fg) / 0.9);
    font-style: italic;
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 1.5rem;
  line-height: 1.7;
  color: rgb(var(--fg) / 0.82);
  font-size: 1rem;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.7em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(77, 159, 255, 0.7);
    box-shadow: 0 0 8px rgba(77, 159, 255, 0.45);
  }

  strong {
    color: rgb(var(--fg) / 0.95);
    font-weight: 700;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
  margin: 0.25rem 0 1.25rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 1.1rem 1.15rem;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgba(77, 159, 255, 0.2);
  border-radius: 14px;
  text-align: left;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${ACCENT};
  letter-spacing: -0.02em;
  margin-bottom: 0.35rem;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.72rem;
  color: rgb(var(--fg) / 0.72);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
`;

const EmbedFrame = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 16px;
  background: rgb(0 0 0 / calc(0.42 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.06);
`;

const EmbedLabel = styled.p`
  margin: 0 0 0.6rem;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${ACCENT};
  font-weight: 700;
`;

const ResponsiveIframeContainer = styled.div`
  position: relative;
  overflow: hidden;
  padding-top: 56.25%;
  border-radius: 10px;
  background: #0a0d12;
`;

const ResponsiveIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 0.5rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 2.2rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(77, 159, 255, 0.5);
  }
`;

const BbdaRegression = () => {
  const tags = ['Python', 'Machine Learning', 'Regression', 'Data Analysis'];
  const datePublished = '2024-07-13';
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <Title>Household Income Prediction Using Regression Techniques</Title>
        <Subtitle>
          A full regression workflow — EDA, preprocessing, model tuning, and interpretability — applied
          to a synthetic household income dataset.
        </Subtitle>
        <DatePublished>Published on {datePublished}</DatePublished>
        <CoverImage src="/images/bbda_reg.png" alt="Household Income Prediction" />
        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <Paragraph>
          This project was conducted as part of the Data Science Bootcamp from Big Blue Data Academy.
          It was the third practice project of the course, and was completed in around 5 hours on the
          13th of July 2024. It involves analyzing and predicting household income using various regression
          techniques. The aim was to explore the relationships between demographic and socioeconomic factors
          and to evaluate the performance of different machine learning models in predicting annual household income.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Objectives</SectionTitle>
        <Paragraph>The primary objectives of this project were:</Paragraph>
        <List>
          <ListItem>Perform exploratory data analysis (EDA) to understand the dataset.</ListItem>
          <ListItem>Preprocess the data appropriately for modeling.</ListItem>
          <ListItem>Build and evaluate regression models for predicting household income.</ListItem>
          <ListItem>Interpret the models using SHAP and LIME for insights into feature importance.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Tools Used</SectionTitle>
        <Paragraph>The following tools and libraries were utilized in this project:</Paragraph>
        <List>
          <ListItem><strong>Python</strong></ListItem>
          <ListItem><strong>Pandas</strong> and <strong>NumPy</strong> for data manipulation</ListItem>
          <ListItem><strong>Matplotlib</strong> and <strong>Seaborn</strong> for data visualization</ListItem>
          <ListItem><strong>scikit-learn</strong> for machine learning models and preprocessing</ListItem>
          <ListItem><strong>XGBoost</strong> for gradient boosting regression</ListItem>
          <ListItem><strong>SHAP</strong> and <strong>LIME</strong> for model interpretability</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Methodology</SectionTitle>
        <Paragraph>The project followed a structured machine learning workflow:</Paragraph>
        <List>
          <ListItem><strong>Exploratory Data Analysis:</strong> Assessed data distributions, identified skewness, and applied log transformations to the target variable.</ListItem>
          <ListItem><strong>Data Preprocessing:</strong> Encoded categorical variables, handled ordinal features, and constructed pipelines for data transformation.</ListItem>
          <ListItem><strong>Model Training:</strong> Evaluated multiple regression models including Linear Regression, Lasso, Ridge, Decision Trees, Random Forest, and XGBoost with hyperparameter tuning using GridSearchCV.</ListItem>
          <ListItem><strong>Model Evaluation:</strong> Used cross-validation with appropriate metrics (MSE, MAE, R²) to assess model performance.</ListItem>
          <ListItem><strong>Interpretability:</strong> Applied SHAP and LIME to interpret the models and understand feature importance and effects.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Results</SectionTitle>
        <StatsGrid>
          <StatCard>
            <StatValue>0.02 – 0.09</StatValue>
            <StatLabel>R² Range Across Models</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>XGBoost</StatValue>
            <StatLabel>Best Performing Model</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>&lt; 10%</StatValue>
            <StatLabel>Variance Explained</StatLabel>
          </StatCard>
        </StatsGrid>
        <Paragraph>
          The models yielded low predictive performance, with R² scores ranging from approximately 0.02 to 0.09.
          The XGBoost model performed slightly better than others but still explained less than 10% of the variance
          in the target variable.
        </Paragraph>
        <Paragraph>Key observations from the analysis include:</Paragraph>
        <List>
          <ListItem>
            <strong>Low Correlations:</strong> Most features had low correlations with the target variable,
            making it challenging for models to learn predictive patterns.
          </ListItem>
          <ListItem>
            <strong>Counterintuitive Findings:</strong> SHAP and Partial Dependence Plots revealed relationships
            that contradicted real-world expectations, suggesting potential issues with the synthetic dataset.
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Model Interpretation</SectionTitle>
        <Paragraph>
          The SHAP analysis highlighted features like <em>Homeownership_Status_Rent</em> and <em>Employment_Status_Part-time</em>
          as significant, but their relationships with income were counterintuitive. LIME provided local explanations for individual
          predictions, but due to the overall low model performance, these interpretations were limited.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Exploratory Data Analysis Notebook</SectionTitle>
        <EmbedFrame>
          <EmbedLabel>Notebook · EDA</EmbedLabel>
          <ResponsiveIframeContainer>
            <ResponsiveIframe src="/embeds/income_analysis.html" title="Income Analysis Notebook" />
          </ResponsiveIframeContainer>
        </EmbedFrame>
      </Section>

      <Section>
        <SectionTitle>Modeling Notebook</SectionTitle>
        <EmbedFrame>
          <EmbedLabel>Notebook · Modeling</EmbedLabel>
          <ResponsiveIframeContainer>
            <ResponsiveIframe src="/embeds/income_modeling.html" title="Income Modeling Notebook" />
          </ResponsiveIframeContainer>
        </EmbedFrame>
      </Section>

      <Section>
        <SectionTitle>Presentation</SectionTitle>
        <EmbedFrame>
          <EmbedLabel>Slides</EmbedLabel>
          <ResponsiveIframeContainer>
            <ResponsiveIframe
              src="https://docs.google.com/presentation/d/e/2PACX-1vR05Irfbnb-iK1vI8c47GaTK4WjbbxUszkuNaAdZVNF5nN0MgRvGhCESL-Vp2JzZw/embed?start=false&loop=false&delayms=3000"
              title="Project Presentation"
              allowFullScreen
            />
          </ResponsiveIframeContainer>
        </EmbedFrame>
      </Section>

      <Section>
        <SectionTitle>Conclusion</SectionTitle>
        <Paragraph>
          The project demonstrated that even with a robust machine learning workflow, the quality and nature of the dataset
          are crucial for model success. The synthetic dataset's lack of meaningful relationships limited the predictive power
          of the models. This emphasizes the importance of data quality and relevance in predictive modeling.
        </Paragraph>
      </Section>

      <ButtonRow>
        <BackButton onClick={() => navigate('/projects')}>← Back to Projects</BackButton>
      </ButtonRow>
    </Container>
  );
};

export const BbdaRegressionMetadata = {
  title: 'Household Income Prediction Using Regression Techniques',
  description:
    'Analyzed and predicted household income using various regression techniques on a synthetic dataset.',
  tags: ['Python', 'Machine Learning', 'Regression', 'Data Analysis'],
  languages: ['Python', 'GitHub'],
  datePublished: '2024-07-13',
  path: '/projects/household-income-prediction',
  coverImage: '/images/bbda_reg.png',
};

export default BbdaRegression;
